import { useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiEdit2,
  FiLayers,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import SubCategoryModal from "../../components/masters/SubCategoryModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return storageKey.startsWith("http")
    ? storageKey
    : `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function SubCategoriesPage() {
  const { accessToken } = useAuth();
  const [subCategories, setSubCategories] = useState([]);
  const [sizeGroups, setSizeGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showModal, setShowModal] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const [subCategoryResponse, categoryResponse, sizeGroupResponse] =
        await Promise.all([
          apiRequest("/subcategories", { accessToken }),
          apiRequest("/categories", { accessToken }),
          apiRequest("/size-groups", { accessToken }),
        ]);

      setSubCategories(
        Array.isArray(subCategoryResponse)
          ? subCategoryResponse
          : subCategoryResponse?.data || [],
      );

      setCategories(
        Array.isArray(categoryResponse)
          ? categoryResponse
          : categoryResponse?.data || [],
      );

      setSizeGroups(
        Array.isArray(sizeGroupResponse)
          ? sizeGroupResponse
          : sizeGroupResponse?.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load subcategories.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [accessToken]);

  const getCategoryName = (subCategory) =>
    subCategory.category?.name ||
    categories.find((category) => category.id === subCategory.categoryId)
      ?.name ||
    "Unknown Category";

  const filteredSubCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return subCategories.filter((subCategory) => {
      const parentName = getCategoryName(subCategory).toLowerCase();

      const matchesSearch =
        !query ||
        subCategory.name.toLowerCase().includes(query) ||
        subCategory.code.toLowerCase().includes(query) ||
        parentName.includes(query);

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && subCategory.isActive) ||
        (statusFilter === "INACTIVE" && !subCategory.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [subCategories, categories, search, statusFilter]);

  const activeCount = subCategories.filter(
    (subCategory) => subCategory.isActive,
  ).length;
  const subCategoriesPagination = useClientPagination(filteredSubCategories);

  const productsLinked = subCategories.reduce(
    (total, subCategory) =>
      total +
      (subCategory._count?.products || subCategory.products?.length || 0),
    0,
  );

  const handleSave = async (payload) => {
    if (selectedSubCategory) {
      await apiRequest(`/subcategories/${selectedSubCategory.id}`, {
        method: "PATCH",
        body: payload,
        accessToken,
      });
    } else {
      await apiRequest("/subcategories", {
        method: "POST",
        body: payload,
        accessToken,
      });
    }

    await loadData();
    setSelectedSubCategory(null);
  };

  const handleDelete = async (subCategory) => {
    if (!window.confirm(`Delete "${subCategory.name}"?`)) return;

    try {
      setError("");

      await apiRequest(`/subcategories/${subCategory.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete subcategory.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Subcategories</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Subcategories Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage all product subcategories in your ERP.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium opacity-60"
          >
            <FiUpload />
            Import
          </button>

          <button
            type="button"
            disabled
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium opacity-60"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedSubCategory(null);
              setShowModal(true);
            }}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 font-medium text-white hover:bg-primary-800"
          >
            <FiPlus />
            Add Subcategory
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <p className="text-sm text-gray-500">Total Subcategories</p>
          <h2 className="mt-2 text-3xl font-bold">{subCategories.length}</h2>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <p className="text-sm text-gray-500">Active</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {activeCount}
          </h2>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <p className="text-sm text-gray-500">Inactive</p>
          <h2 className="mt-2 text-3xl font-bold text-red-500">
            {subCategories.length - activeCount}
          </h2>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <p className="text-sm text-gray-500">Products Linked</p>
          <h2 className="mt-2 text-3xl font-bold">{productsLinked}</h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-96">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="text"
              placeholder="Search subcategories..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        {error && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Subcategory
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Code
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Parent Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Products
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading subcategories...
                  </td>
                </tr>
              ) : filteredSubCategories.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No subcategories found.
                  </td>
                </tr>
              ) : (
                subCategoriesPagination.pageItems.map((subCategory) => (
                  <tr
                    key={subCategory.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 text-primary-700">
                          {subCategory.imageKey ? (
                            <img
                              src={getImageUrl(subCategory.imageKey)}
                              alt={subCategory.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <FiLayers size={20} />
                          )}
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {subCategory.name}
                          </h4>

                          <p className="text-xs text-gray-500">
                            {subCategory.description || "Product Subcategory"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 font-medium">
                      {subCategory.code}
                    </td>

                    <td className="px-6 py-4">
                      {getCategoryName(subCategory)}
                    </td>

                    <td className="px-6 py-4">
                      {subCategory._count?.products ||
                        subCategory.products?.length ||
                        0}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          subCategory.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {subCategory.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSubCategory(subCategory);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(subCategory)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-red-500 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={subCategoriesPagination} label="sub-categories" />

        <div className="p-5">
          <p className="text-sm text-gray-500">
            Showing {filteredSubCategories.length} subcategories
          </p>
        </div>
      </div>

      <SubCategoryModal
        open={showModal}
        subCategory={selectedSubCategory}
        categories={categories}
        sizeGroups={sizeGroups}
        accessToken={accessToken}
        onClose={() => {
          setShowModal(false);
          setSelectedSubCategory(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}
